// =============================================================================
// PAGE MOBILE DAL - Accès simplifié par Code Dossier + PIN
// Avec support mode hors-ligne (PWA)
// =============================================================================

import { useState, useEffect } from "react";
import { 
  Smartphone, 
  KeyRound, 
  FileText, 
  ArrowRight, 
  AlertCircle,
  Loader2,
  Home,
  CheckCircle2,
  WifiOff,
  Wifi,
  RefreshCw,
  CloudOff,
  Cloud
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import MobileDALWizard from "../../components/dal/MobileDALWizard";
import offlineStorage from "../../utils/offlineStorage";
import { formatClientFullName } from "../../utils/clientFormat";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MobileDALPage() {
  // État authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Formulaire d'accès
  const [dossierCode, setDossierCode] = useState("");
  const [pin, setPin] = useState("");
  
  // Données dossier
  const [dossierData, setDossierData] = useState(null);
  
  // DAL complété
  const [dalCompleted, setDalCompleted] = useState(false);
  
  // État hors-ligne
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [aRevoir, setARevoir] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // Enregistrer le Service Worker + manifest DAL
  useEffect(() => {
    // Switch manifest to DAL-specific one
    try {
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) manifestLink.href = '/dal-manifest.json';
      const themeColor = document.querySelector('meta[name="theme-color"]');
      if (themeColor) themeColor.content = '#0a0a14';
    } catch (e) { /* ignore DOM errors */ }
    
    // PREPROD (env de test) : pas de SW → un reload montre toujours le dernier
    // build (cf. public/sw.js kill-switch + public/index.html). PROD : PWA offline.
    const IS_PREPROD = /(^|\.)preprod\./i.test(window.location.hostname);
    if ('serviceWorker' in navigator) {
      if (IS_PREPROD) {
        navigator.serviceWorker.getRegistrations()
          .then((regs) => regs.forEach((r) => r.unregister()))
          .catch(() => {});
      } else {
        navigator.serviceWorker.register('/sw.js')
          .then((reg) => console.log('[DAL-PWA] SW enregistre:', reg.scope))
          .catch((err) => console.warn('[DAL-PWA] SW error:', err));
      }
    }
    
    // Initialiser le stockage hors-ligne (non bloquant)
    try {
      offlineStorage.init().then(() => {
        checkPendingSync();
      }).catch(() => {});
    } catch (e) {
      console.warn('[DAL-PWA] Stockage hors-ligne indisponible');
    }
  }, []);

  // Écouter les changements de connexion
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncData();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Vérifier les données en attente de sync
  const checkPendingSync = async () => {
    const count = await offlineStorage.getPendingCount();
    setPendingSync(count);
    // N°546 — les saisies REFUSÉES (conflit / révision absente) : visibles.
    setARevoir(await offlineStorage.getARevoir());
  };

  // Synchroniser les données
  const syncData = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    try {
      await offlineStorage.syncPendingData();
      await checkPendingSync();
    } catch (error) {
      console.error('[PWA] Erreur sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Auth via QR access token ou restauration de session existante
  // Supporte aussi ?code={ref}&pin={pin} pour lien pré-rempli
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access");
    const urlCode = params.get("code");
    const urlPin = params.get("pin");

    // Mode 1: access token (QR code)
    if (accessToken) {
      setLoading(true);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/dal/mobile-auth`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            const session = { dossier: data.dossier, token: data.token, expires: Date.now() + 86400000 };
            localStorage.setItem("mobile_dal_session", JSON.stringify(session));
            localStorage.setItem("mobile_dal_token", data.token);
            setDossierData(data.dossier);
            setIsAuthenticated(true);
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            setError(data.detail || "Token d'accès invalide");
          }
        } catch (e) { setError("Erreur de communication avec le serveur"); }
        setLoading(false);
      };
      xhr.onerror = function() { setError("Erreur réseau"); setLoading(false); };
      xhr.send(JSON.stringify({ access_token: accessToken }));
      return;
    }

    // Mode 2: code + pin pré-remplis (lien SMS/email)
    if (urlCode && urlPin) {
      setDossierCode(urlCode);
      setPin(urlPin);
      // Auto-login
      setLoading(true);
      setError(null);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/api/dal/mobile-auth`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            const session = { dossier: data.dossier, token: data.token, expires: Date.now() + 86400000 };
            localStorage.setItem("mobile_dal_session", JSON.stringify(session));
            localStorage.setItem("mobile_dal_token", data.token);
            setDossierData(data.dossier);
            setIsAuthenticated(true);
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            setError(data.detail || "Code ou PIN invalide");
          }
        } catch (e) { setError("Erreur de communication avec le serveur"); }
        setLoading(false);
      };
      xhr.onerror = function() { setError("Erreur réseau"); setLoading(false); };
      xhr.send(JSON.stringify({ dossier_code: urlCode.toUpperCase().trim(), pin: urlPin.trim() }));
      return;
    }

    // Mode 3: session restaurée
    const savedSession = localStorage.getItem("mobile_dal_session");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.expires > Date.now()) {
          setDossierData(session.dossier);
          setIsAuthenticated(true);
          localStorage.setItem("mobile_dal_token", session.token);
          loadOfflineData(session.dossier.id);
        } else {
          localStorage.removeItem("mobile_dal_session");
          localStorage.removeItem("mobile_dal_token");
        }
      } catch (e) {
        localStorage.removeItem("mobile_dal_session");
        localStorage.removeItem("mobile_dal_token");
      }
    }
  }, []);

  // Charger les données hors-ligne
  const loadOfflineData = async (dossierId) => {
    try {
      const offlineData = await offlineStorage.getDALData(dossierId);
      if (offlineData && offlineData.data) {
        console.log('[PWA] Données hors-ligne chargées');
        // Fusionner avec les données existantes si plus récentes
        setDossierData(prev => ({
          ...prev,
          dal_data: {
            ...prev?.dal_data,
            ...offlineData.data
          }
        }));
      }
    } catch (error) {
      console.error('[PWA] Erreur chargement hors-ligne:', error);
    }
  };

  // Authentification par code dossier + PIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use XMLHttpRequest to avoid service worker issues
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/dal/mobile-auth`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        
        xhr.onload = function() {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.detail || "Code ou PIN invalide"));
            }
          } catch (parseError) {
            reject(new Error("Erreur de communication avec le serveur"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Erreur réseau"));
        };
        
        xhr.send(JSON.stringify({
          dossier_code: dossierCode.toUpperCase().trim(),
          pin: pin.trim()
        }));
      });

      // Sauvegarder la session (24h)
      const session = {
        dossier: result.dossier,
        token: result.token,
        expires: Date.now() + 24 * 60 * 60 * 1000
      };
      localStorage.setItem("mobile_dal_session", JSON.stringify(session));
      localStorage.setItem("mobile_dal_token", result.token);
      
      // Sauvegarder les données pour le mode hors-ligne
      try {
        if (result.dossier?.id) {
          await offlineStorage.saveDALData(
            result.dossier.id,
            result.dossier.tenant_id,
            result.dossier.dal_data || {}
          );
        }
      } catch (offlineErr) {
        console.warn('[DAL] Sauvegarde hors-ligne échouée:', offlineErr);
      }
      
      setDossierData(result.dossier);
      setIsAuthenticated(true);

    } catch (err) {
      // En mode hors-ligne, vérifier si on a des données locales
      if (!isOnline) {
        setError("Mode hors-ligne: connexion requise pour la première authentification");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const handleLogout = () => {
    localStorage.removeItem("mobile_dal_session");
    localStorage.removeItem("mobile_dal_token");
    setIsAuthenticated(false);
    setDossierData(null);
    setDossierCode("");
    setPin("");
  };

  // DAL terminé — n'est appelé QUE si saveDAL a retourné ok=true côté wizard
  const handleDALComplete = async (data) => {
    setDalCompleted(true);

    // Sauvegarder localement (cache offline, indépendant du statut réseau)
    if (dossierData) {
      try {
        await offlineStorage.saveDALData(
          dossierData.id,
          dossierData.tenant_id,
          data
        );

        // Si hors-ligne, ajouter à la file de sync
        // 🔴 N°546 — on met en file le CORPS RÉELLEMENT ENVOYÉ, avec sa
        // `base_revision` (⛔ plus l'objet d'écran, qui n'avait pas la forme du
        // POST). Rejoué plus tard sur un DAL modifié entre-temps, il sera
        // REFUSÉ (409) — et ⛔ jamais silencieusement appliqué.
        if (!isOnline) {
          await offlineStorage.addPendingSync(
            dossierData.id,
            dossierData.tenant_id,
            'save_dal',
            data?.uploadStats?.corpsPost || data
          );
          await checkPendingSync();
        }
      } catch (e) {
        console.warn("[DAL-PWA] Sauvegarde locale échouée:", e);
      }
    }

    console.log("DAL complété:", data?.uploadStats);
  };

  // Indicateur de statut en ligne
  const OnlineIndicator = () => (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
      isOnline ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
    }`}>
      {isOnline ? (
        <>
          <Wifi className="w-3 h-3" />
          En ligne
        </>
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          Hors ligne
        </>
      )}
      {pendingSync > 0 && (
        <span className="ml-1 bg-orange-500/30 px-1.5 py-0.5 rounded-full">
          {pendingSync} en attente
        </span>
      )}
      {syncing && <RefreshCw className="w-3 h-3 animate-spin ml-1" />}
    </div>
  );

  // 🔴 N°546 — SAISIES HORS LIGNE À REVOIR. Le serveur a REFUSÉ de les
  // appliquer (le DAL a changé depuis, ou elles ne disent pas sur quelle
  // version elles s'appuient). ⛔ Elles ne sont NI supprimées NI rejouées :
  // Jordan DOIT voir qu'une saisie attend une résolution.
  const SaisiesARevoir = () => (aRevoir.length === 0 ? null : (
    <div
      role="alert"
      data-testid="dal-saisies-a-revoir"
      className="fixed left-4 right-4 z-50"
      style={{
        bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
        background: "var(--oh-error-bg)", color: "var(--oh-error-text)",
        border: "0.5px solid var(--oh-error-border)", borderRadius: 8, padding: 12,
        lineHeight: 1.6,
      }}
    >
      <p style={{ fontWeight: 500, margin: 0 }}>
        {aRevoir.length === 1 ? "1 saisie hors ligne à revoir" : `${aRevoir.length} saisies hors ligne à revoir`}
        {" "}— conflit de version
      </p>
      <p style={{ margin: "4px 0 0", fontSize: 12 }}>
        Le DAL a été modifié depuis cette saisie : elle n'a pas été appliquée,
        pour ne pas écraser une saisie plus récente. Vos données sont conservées
        sur cet appareil.
      </p>
      {aRevoir.map(i => (
        <p key={i.id} style={{ margin: "4px 0 0", fontSize: 12 }}>
          · Saisie du {new Date(i.timestamp).toLocaleString("fr-FR")} — {i.refus?.motif || `refus ${i.refus?.http || ""}`}
        </p>
      ))}
    </div>
  ));

  // Page de succès
  if (dalCompleted) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6">
        <OnlineIndicator />
        <SaisiesARevoir />
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <h1 className="text-white text-2xl font-bold mb-2">DAL Complété !</h1>
        <p className="text-slate-400 text-center mb-4">
          Les informations ont été sauvegardées.<br/>
          Le DAL PDF sera généré automatiquement.
        </p>
        
        {!isOnline && pendingSync > 0 && (
          <div className="bg-orange-500/20 border border-orange-500/30 rounded-lg p-3 mb-6 flex items-center gap-2">
            <CloudOff className="w-5 h-5 text-orange-400" />
            <p className="text-orange-300 text-sm">
              Données sauvegardées localement. Synchronisation automatique au retour en ligne.
            </p>
          </div>
        )}
        
        {isOnline && pendingSync === 0 && (
          <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-6 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-green-400" />
            <p className="text-green-300 text-sm">
              Toutes les données sont synchronisées.
            </p>
          </div>
        )}
        
        <div className="space-y-3 w-full max-w-xs">
          <Button 
            onClick={() => setDalCompleted(false)}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Modifier le DAL
          </Button>
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="w-full"
          >
            Terminer et quitter
          </Button>
        </div>
      </div>
    );
  }

  // Formulaire DAL
  if (isAuthenticated && dossierData) {
    return (
      <>
        <OnlineIndicator />
        <SaisiesARevoir />
        <MobileDALWizard
          dossierId={dossierData.id}
          tenantId={dossierData.tenant_id}
          clientName={formatClientFullName(dossierData.client) || "Client"}
          clientInfo={dossierData.client || {}}
          dossierInfo={{
            reference: dossierData.reference,
            type_projet: dossierData.type_projet,
            logement: dossierData.logement,
            surface_habitable: dossierData.surface_habitable,
            nb_pieces: dossierData.nb_pieces,
            // Point 4 — âge MPA dérivé serveur (date_naissance) pour pré-remplir
            // l'âge en lecture seule côté terrain quand disponible.
            eligibilite_mpa_age: dossierData.eligibilite_mpa?.age ?? null,
          }}
          initialData={dossierData.dal_data}
          visiteurAmo={dossierData.visiteur_amo}
          onComplete={handleDALComplete}
          onCancel={handleLogout}
          isOffline={!isOnline}
        />
      </>
    );
  }

  // Page de connexion
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex flex-col">
      <OnlineIndicator />
      <SaisiesARevoir />
      
      {/* Header */}
      <header className="p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
          <Smartphone className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-white text-2xl font-bold">DAL Mobile</h1>
        <p className="text-slate-400 mt-1">Diagnostic Autonomie Logement</p>
      </header>

      {/* Formulaire */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Code dossier */}
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Code Dossier
              </Label>
              <Input
                type="text"
                value={dossierCode}
                onChange={(e) => setDossierCode(e.target.value.toUpperCase())}
                placeholder="Ex: TEF-2026-001"
                className="bg-slate-800/50 border-slate-700 text-center text-lg tracking-wider uppercase"
                required
                autoComplete="off"
              />
            </div>

            {/* PIN */}
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                Code PIN
              </Label>
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="bg-slate-800/50 border-slate-700 text-center text-2xl tracking-[0.5em]"
                required
                autoComplete="off"
              />
              <p className="text-slate-500 text-xs text-center">
                Code à 4-6 chiffres fourni par votre responsable
              </p>
            </div>

            {/* Avertissement hors-ligne */}
            {!isOnline && (
              <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-3 flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <p className="text-orange-300 text-sm">
                  Mode hors-ligne. Connectez-vous une première fois en ligne.
                </p>
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Bouton */}
            <Button
              type="submit"
              disabled={loading || !dossierCode || !pin || (!isOnline && !localStorage.getItem("mobile_dal_session"))}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  Accéder au DAL
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm">
              Besoin d'aide ? Contactez votre responsable
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center">
        <p className="text-slate-600 text-xs">
          OmniHub © 2026 - Transition Énergie France
        </p>
      </footer>
    </div>
  );
}
